import { Lightbox, type Slide } from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import type { AttachmentPreview } from './types'

type UnifiedPreviewProps = {
  open: boolean
  onClose: () => void
  attachments: AttachmentPreview[]
  initialIndex: number
  onIndexChange: (index: number) => void
}

type CustomSlide = Slide & {
  isVideo?: boolean
  isPdf?: boolean
  fileName?: string
  videoType?: string
}

export function UnifiedPreview({
  open,
  onClose,
  attachments,
  initialIndex,
  onIndexChange,
}: UnifiedPreviewProps) {
  if (!open) return null

  const slides: CustomSlide[] = attachments.map((attachment) => {
    const { type, previewUrl, file } = attachment

    if (type === 'image' || type === 'sticker') {
      return {
        src: previewUrl || '',
      }
    }

    if (type === 'video') {
      return {
        src: previewUrl || '',
        isVideo: true,
        videoType: file.type,
        fileName: file.name,
      }
    }

    if (type === 'document') {
      const isPdf = file.type === 'application/pdf'
      if (isPdf) {
        const objectUrl = previewUrl || URL.createObjectURL(file)
        return {
          src: objectUrl,
          isPdf: true,
          fileName: file.name,
        }
      }
    }

    return {
      src: '',
    }
  })

  const renderSlide = ({ slide }: { slide: CustomSlide }) => {
    if (slide.isVideo && 'src' in slide) {
      return (
        <div className='flex items-center justify-center w-full h-full'>
          <video
            src={slide.src}
            controls
            className='max-w-full max-h-full'
            playsInline
            autoPlay={false}
          >
            <track kind='captions' />
          </video>
        </div>
      )
    }

    if (slide.isPdf && 'src' in slide) {
      return (
        <div className='flex flex-col gap-4 items-center justify-center w-full h-full p-4'>
          <iframe
            src={slide.src}
            title={slide.fileName || 'PDF'}
            className='w-full h-full bg-white rounded-lg'
          />
        </div>
      )
    }
    return undefined
  }

  return (
    <Lightbox
      open={open}
      close={onClose}
      slides={slides}
      index={initialIndex}
      on={{ view: ({ index }) => onIndexChange(index) }}
      carousel={{ finite: true }}
      render={{
        slide: renderSlide,
      }}
    />
  )
}
